import { Fragment, useEffect, useRef, useState } from 'react';
import Image from 'next/legacy/image';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { Carousel } from 'antd';
import { LeftOutlined, RightOutlined, StarFilled } from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';
import TextClamp from 'react-string-clamp';

import { ITestimonial } from '../../common/model/testimonial.model';

type Props = {
  testimonials: ITestimonial[];
  reducedWidth?: boolean;
  theme: DefaultTheme;
};

const Card = styled.div<{ $reducedWidth: boolean }>`
  position: relative;
  background-color: white;
  border-radius: ${(props) => props.theme.borderRadius};
  margin: 24px;
  padding: 32px 80px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: ${(props) => (props.$reducedWidth ? '406px' : '310px')};
  box-shadow: 0px 6px 21px -4px rgba(0, 0, 0, 0.25);

  font-size: 18px;
  line-height: 20px;

  @media ${(props) => props.theme.breakpoints.smd} {
    height: 280px;
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    padding: 32px 40px;
  }

  .img {
    border-radius: 50%;
  }

  .description {
    font-style: italic !important;
    color: #828282;
    margin-bottom: 15px;
    margin-top: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .title {
    margin-left: 12px;
    font-style: italic !important;
    color: #828282;
    ${(props) => props.theme.font.p1};
    font-weight: 700 !important;
  }

  .stars {
    font-size: 16px;
    color: ${(props) => props.theme.colors.primary};
    @media ${(props) => props.theme.breakpoints.xs} {
      font-size: 14px;
    }
  }

  .source {
    position: relative;
    width: 80px;
    height: 53px;

    @media ${(props) => props.theme.breakpoints.xs} {
      width: 40px;
      height: 27px;
    }
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    font-size: 12px;
    line-height: 14px;
  }
`;

const PrevArrow = styled(LeftOutlined)`
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  top: calc(50% - 20px);
  position: absolute;
  width: 40px !important;
  height: 40px !important;
  left: 0 !important;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NextArrow = styled(RightOutlined)`
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  top: calc(50% - 20px);
  position: absolute;
  width: 40px !important;
  height: 40px !important;
  right: 0 !important;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardFirstRowContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const CardThirdRowContainer = styled.div`
  height: 40px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TestimonialsCarousel: React.FC<Props> = ({
  testimonials,
  reducedWidth = false,
  theme,
}) => {
  const carousel = useRef(null);
  const isSmd = useMediaQuery({ query: theme.breakpoints.smd });

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  let lines = reducedWidth ? 11 : 8;
  if (isMounted && isSmd) {
    lines = 8;
  }

  const items = testimonials.map((t) => ({
    content: (
      <Card as="div" $reducedWidth={reducedWidth}>
        <CardFirstRowContainer>
          <Image
            alt={t.name}
            height={35}
            width={35}
            className="img"
            src={t.imageUrl}
          />
          <div className="title">{t.name}</div>
        </CardFirstRowContainer>
        <div className="description">
          <TextClamp text={t.description} lines={lines} />
        </div>
        <CardThirdRowContainer>
          <div className="stars">
            <StarFilled /> <StarFilled /> <StarFilled /> <StarFilled />{' '}
            <StarFilled />
          </div>
          <div className="source">
            <Image
              alt="google_my_business.png"
              layout="fill"
              src="/images/testimonials/google_my_business.png"
            />
          </div>
        </CardThirdRowContainer>
        <PrevArrow
          onClick={() => {
            carousel.current.prev();
          }}
        />
        <NextArrow
          onClick={() => {
            carousel.current.next();
          }}
        />
      </Card>
    ),
  }));

  return (
    <div style={{ marginLeft: '-24px', marginRight: '-24px' }}>
      <Carousel ref={carousel} dots={false} arrows={false} autoplay={true}>
        {items.map((item, key) => {
          return <Fragment key={key}>{item.content}</Fragment>;
        })}
      </Carousel>
    </div>
  );
};

export default withTheme(TestimonialsCarousel);
